import { useState, useRef, useEffect } from 'react';
import { FotoboxRecord } from '../types';
import BoxCard from './BoxCard';

interface BoxGridProps {
  boxes: FotoboxRecord[];
  isAdmin: boolean;
}

type BrandFilter = 'all' | 'fbt' | 'schickbox';

function getBoxBrand(box: FotoboxRecord): 'fbt' | 'schickbox' | 'other' {
  const id = box.id.toUpperCase();
  const name = box.name.toUpperCase();
  if (id.startsWith('FBTM') || id.startsWith('SB') || id.startsWith('SCH') || name.includes('SCHICK')) {
    return 'schickbox';
  }
  if (id.startsWith('FBTN') || name.includes('FBT') || name.includes('FOTOBOX TEAM')) {
    return 'fbt';
  }
  return 'other';
}

function isNotConnected(box: FotoboxRecord): boolean {
  if (!box.printer_status || box.printer_status === 'unknown') return true;
  if (box.printer_status === 'error' && box.printer_error === 'Drucker nicht angeschlossen') return true;
  return false;
}

function sortBoxes(boxes: FotoboxRecord[]): FotoboxRecord[] {
  return [...boxes].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
    const prioA = (a.printer_status === 'error' && !isNotConnected(a)) ? 0 : a.printer_status === 'warning' ? 1 : 2;
    const prioB = (b.printer_status === 'error' && !isNotConnected(b)) ? 0 : b.printer_status === 'warning' ? 1 : 2;
    if (prioA !== prioB) return prioA - prioB;
    return a.name.localeCompare(b.name, 'de');
  });
}

/** Dropdown mit Checkbox-Liste zum gezielten Auswählen einzelner Boxen */
function BoxSelector({
  boxes,
  selected,
  onChange,
}: {
  boxes: FotoboxRecord[];
  selected: Set<string>;
  onChange: (ids: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Schließen bei Klick außerhalb
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const activeCount = selected.size;
  const allSelected = activeCount === 0;

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  function reset() {
    onChange(new Set());
    setOpen(false);
  }

  function selectAll() {
    onChange(new Set());
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`filter-btn gap-1.5 ${!allSelected ? 'active' : ''}`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 8h10M10 12h4" />
        </svg>
        {allSelected ? 'Auswahl' : `${activeCount} ausgewählt`}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-30 bg-white border border-surface-border rounded-2xl shadow-lg min-w-[220px] max-w-[280px] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-surface-border bg-surface-subtle">
            <span className="font-opensans text-xs font-semibold text-ink-secondary uppercase tracking-wider">
              Boxen filtern
            </span>
            {!allSelected && (
              <button
                onClick={reset}
                className="text-[11px] font-opensans text-brand-green hover:underline"
              >
                Alle zeigen
              </button>
            )}
          </div>

          {/* Alle-Option */}
          <button
            onClick={selectAll}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-subtle transition-colors border-b border-surface-border ${allSelected ? 'text-brand-green' : 'text-ink-secondary'}`}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${allSelected ? 'bg-brand-green border-brand-green' : 'border-surface-border-dark'}`}>
              {allSelected && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <span className="font-opensans text-sm font-medium">Alle Boxen</span>
          </button>

          {/* Box-Liste */}
          <div className="max-h-64 overflow-y-auto py-1">
            {sortBoxes(boxes).map(box => {
              const checked = selected.has(box.id);
              const isOffline = box.status === 'offline';
              return (
                <button
                  key={box.id}
                  onClick={() => toggle(box.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-subtle transition-colors"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-brand-green border-brand-green' : 'border-surface-border-dark'}`}>
                    {checked && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className={`flex-1 min-w-0 font-opensans text-sm truncate ${isOffline ? 'text-ink-faint' : 'text-ink-primary'}`}>
                    {box.name}
                  </span>
                  {/* Status-Dot */}
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOffline ? 'bg-ink-faint' : 'bg-brand-green'}`} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-5">
      <div className="w-16 h-16 rounded-2xl bg-surface-subtle border border-surface-border flex items-center justify-center">
        <svg className="w-8 h-8 text-ink-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
        </svg>
      </div>
      <div className="text-center">
        <p className="font-raleway font-thin text-base uppercase tracking-widest text-ink-secondary">
          Keine Fotoboxen verbunden
        </p>
        <p className="text-ink-muted text-sm mt-1">
          Starte den Agenten auf einer Fotobox, um sie hier zu sehen.
        </p>
      </div>
      <div className="mt-2 px-4 py-2 rounded-xl bg-surface-subtle border border-surface-border">
        <code className="font-mono text-xs text-brand-green">
          POST /api/heartbeat  ←  Fotobox-Agent
        </code>
      </div>
    </div>
  );
}

export default function BoxGrid({ boxes, isAdmin }: BoxGridProps) {
  const [brandFilter, setBrandFilter] = useState<BrandFilter>('all');
  // Set mit ausgewählten Box-IDs – leer = alle anzeigen
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  if (boxes.length === 0) {
    return <EmptyState />;
  }

  const fbtCount = boxes.filter(b => getBoxBrand(b) === 'fbt').length;
  const schickCount = boxes.filter(b => getBoxBrand(b) === 'schickbox').length;
  const hasBrandFilter = fbtCount > 0 && schickCount > 0;

  // Schritt 1: Brand-Filter
  const afterBrand = brandFilter === 'all'
    ? boxes
    : brandFilter === 'fbt'
      ? boxes.filter(b => getBoxBrand(b) === 'fbt')
      : boxes.filter(b => getBoxBrand(b) === 'schickbox');

  // Schritt 2: Einzel-Auswahl (wenn leer → alle)
  const filtered = selectedIds.size > 0
    ? afterBrand.filter(b => selectedIds.has(b.id))
    : afterBrand;

  const sorted = sortBoxes(filtered);

  // Wenn Brand-Filter wechselt, Einzel-Auswahl zurücksetzen
  function handleBrandFilter(f: BrandFilter) {
    setBrandFilter(f);
    setSelectedIds(new Set());
  }

  return (
    <div className="space-y-3">
      {/* Filter-Leiste */}
      <div className="flex items-center gap-2 flex-wrap pb-1">
        {hasBrandFilter && (
          <>
            <button
              className={`filter-btn ${brandFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleBrandFilter('all')}
            >
              Alle ({boxes.length})
            </button>
            <button
              className={`filter-btn ${brandFilter === 'fbt' ? 'active' : ''}`}
              onClick={() => handleBrandFilter('fbt')}
            >
              <img src="/fbt.png" alt="FBT" className="w-4 h-4 object-contain" />
              FBT-Boxen ({fbtCount})
            </button>
            <button
              className={`filter-btn ${brandFilter === 'schickbox' ? 'active' : ''}`}
              onClick={() => handleBrandFilter('schickbox')}
            >
              <img src="/schickbox.png" alt="Schickbox" className="w-4 h-4 object-contain" />
              Schickboxen ({schickCount})
            </button>
            <div className="w-px h-5 bg-surface-border mx-0.5" />
          </>
        )}

        {/* Einzel-Box-Auswahl */}
        <BoxSelector
          boxes={afterBrand}
          selected={selectedIds}
          onChange={setSelectedIds}
        />

        {/* Anzeige aktiver Filter */}
        {selectedIds.size > 0 && (
          <span className="font-opensans text-xs text-ink-muted ml-1">
            {sorted.length} von {afterBrand.length} Boxen
          </span>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-12 text-ink-muted font-opensans text-sm">
          Keine Boxen für diesen Filter gefunden.
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
          {sorted.map((box, i) => (
            <BoxCard key={box.id} box={box} index={i} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}
